<template>
  <div class="page-container">
    <nav class="better-nav has-shadow">
      <div
        :class="{ container: container, 'nav-container': !container }"
        class="d-flex justify-space-between flex-wrap">
          <router-link to="/" class="fw-300 color-white better-nav-item fs-6 pt-4 pb-4 pl-4">
            ClassForward
          </router-link>

          <div
            v-if="!userAuthenticated"
            class="d-flex justify-content-end p-relative mr-4"
            :class="{ 'is-active': navToggled }">
            <router-link
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/login"
              exact
              >Login</router-link>
            <router-link
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/signup"
              exact
              >Signup</router-link>
          </div>

          <div
            v-if="userAuthenticated"
            class="d-flex justify-content-end p-relative ml-2 grow-1"
            :class="{ 'is-active': navToggled }">
            <router-link
              v-if="emailConfirmed"
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/"
              exact
              >Home</router-link>
            <router-link
              v-if="showColleaguesPage && emailConfirmed"
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/colleagues"
              >Colleagues</router-link>
            <router-link
              v-if="emailConfirmed"
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/groups"
              >Groups</router-link>
            <router-link
              v-if="hasExercisesPerm"
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/exercise"
              >ALC</router-link>
            <router-link
              v-if="userIsGroupAdmin"
              class="better-nav-item is-tab"
              active-class="is-active"
              to="/manage"
              >Manage</router-link>
            <section class="better-nav-item is-tab">
              <NavbarNotifications />
            </section>
            <div
              ref="dropdown"
              @click="show = !show"
              class="better-nav-item">
              <img
                 class="user-profile-image magic-button cursor-pointer"
                 :src="avatarURL"/>

             <TransitionFade>
              <ul class="menu-dropdown flex-column min-width-150px" v-show="show && !showInviteModal">
                <li>
                  <router-link to="/tags">Tags</router-link>
                </li>
                <li>
                  <a href="https://support.classforward.com">Support</a>
                </li>
                <li>
                  <router-link to="/settings">Settings</router-link>
                </li>
                <a v-if="canInvite"
                  class="button is-outlined is-info m-2"
                  @click="showInviteModal = true; show = false">
                  + Invite
                </a>
                <a
                  class="button m-2"
                  :class="{ 'is-loading': loggingOut }"
                  @click="sendLogout">
                  Logout
                </a>
              </ul>
             </TransitionFade>
             <InviteModal
                v-if="canInvite"
                @close="showInviteModal = false"
                v-show="showInviteModal && canInvite"
              />
            </div>
          </div>
      </div>
    </nav>

    <div class="pl-4 pt-4 pr-4 pb-2 d-flex direction-column flex-grow-1">
    <slot></slot>
    </div>

    <footer class="footer sticky-footer">
      <div class="container">
        <div class="content has-text-centered">
          <p>
            © 2020 Autumnate Inc.
          </p>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import NavbarNotifications from './NavbarNotifications.vue'
import TransitionFade from './TransitionFade'
import InviteModal from './InviteModal'

import store from '../store'

import posthog from 'posthog-js'

import {
  logout,
} from '../actionTypes'

export default {
  name: 'BasePage',
  components: {
    NavbarNotifications,
    TransitionFade,
    InviteModal,
  },
  data () {
    return {
      show: false,
      navToggled: false,
      loggingOut: false,
      showInviteModal: false,
    }
  },
  props: {
    container: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: false,
    },
  },
  async mounted () {
    document.title = this.title != null
      ? `${this.title} | ClassForward`
      : 'ClassForward'
    document.addEventListener('mouseup', this.handleGeneralClick)
    await store.dispatch('fetchGroup')
    posthog.identify(store.state.user.id)
    posthog.people.set({ email: store.state.user.email })
  },
  beforeDestroy () {
    document.removeEventListener('mouseup', this.handleGeneralClick)
  },
  methods: {
    handleGeneralClick (e) {
      const element = this.$refs.dropdown
      if (element == null) return
      const clickedInComponent = element.contains(e.target)
      if (clickedInComponent) return
      this.show = false
    },
    async sendLogout () {
      this.loggingOut = true
      await store.dispatch(logout)
      this.loggingOut = false
    },
  },
  computed: {
    ...mapGetters([
      'emailConfirmed',
    ]),
    userAuthenticated () {
      return store.state.user.authenticated
    },
    userIsGroupAdmin () {
      return store.state.user.is_group_admin
    },
    showColleaguesPage () {
      return store.state.user.is_diy || store.state.group.use_colleagues
    },
    avatarURL () {
      const avatarURL = store.state.user.avatar_url
      return avatarURL != null ? avatarURL : ''
    },
    exercisesEnabled () {
      return store.state.flags && store.state.flags.includes('exercises')
    },
    canInvite () {
      return store.state.user.user_can_invite
    },
    hasExercisesPerm () {
      return store.state.user.perm_exercises
    },
  },
}
</script>

<style lang="scss" >
@import "~bulma/sass/utilities/initial-variables";
@import "~balloon-css/balloon.css";

@import "~loaders.css";
// define custom variables

@import '../_variables.scss';

$primary: $teal;

// source overrides after bulma so they aren't overwritten by bulma

@import "../main.scss";

.logo {
  font-size: 1.4rem;
  font-weight: 300;
}

.page-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.sticky-footer {
  width: 100%;
  padding: 1rem;
}

.section {
  flex-grow: 1;
  padding-top: 1rem;
}

// override default bulma nav item
a.better-nav-item:not(.button) {
  color: white;

  &:hover {
    color: darken(white, 7%);
  }

  &:active {
    color: darken(white, 10%);
  }
}


a.better-nav-item:not(.button).is-tab.is-active {
  $activeTeal: mix($teal, white, 30%);
  color: $activeTeal;
  border-bottom-color: $activeTeal;
}

/* need to disable flex-basis as it results in cropping of the nav bar items */
@media screen and (min-width: 1192px) {
  .nav-left, .nav-right {
    flex-basis: unset;
  }
}

/* need to override background so that the teal X can be seen against the
background */
.nav-toggle.is-active {
  background-color: whitesmoke;
}

.nav-container {
  display: flex;
  width: initial;
  max-width: initial;
  // from bulma's .nav > .container
  min-height: 3.25rem;
}

// Bulma override to make specifying content easier
section.section {
  background-color: inherit;
}


.fade-enter-active, .fade-leave-active {
  transition: all .1s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.menu-dropdown {
  display: flex;
  position: absolute;
  border-radius: 3px;
  top: 100%;
  right: 0;
  z-index: 100;
  cursor: default;
  margin-top: 5px;
  background-color: white;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);

  & > li {
    display: flex;
  }

  & > li > a {
    padding: .5rem 1rem .5rem 1rem;
  }
}


.user-profile-image {
  user-select: none;


  &-image {
    $height: 36px;
    height: $height;
    width: $height;
    margin: 5px;
    border-radius: 5px;
  }

  $height: 38px;
  height: $height;
  min-height: $height;
  width: $height;
  min-width: $height;
  margin: 5px;
  border-radius: 100%;
  background: white;
  border-width: 2px;
  border-style: solid;
  border-color: white;
}

// bulma override
.nav {
  height: initial;
}

// bulma override
.nav-right {
  overflow: initial;
}


// bulma override
div.better-nav-item img {
  max-height: none;
}


.flex-column {
  flex-direction: column;
}

.better-nav {
  align-items: stretch;
  position: relative;
  text-align: center;
  // make sure this is larger than the .tarp and .makers in the video element
  z-index: 20;
  background-color: $teal;

  &.has-shadow {
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  }
}

.better-nav-item {
  align-items: center;
  display: flex;
  font-size: 1.1rem;
  line-height: 1.5;
  padding: 0.3rem;
  font-weight: 500;
}

.fw-300 {
  font-weight: 300;
}

.color-white {
  color: white !important;
}

</style>



// WEBPACK FOOTER //
// BasePage.vue?0c842b24